import {
  ArtistProfileStatus,
  WorkItemStatus,
  type WorkItemDto
} from '@customTypes/types';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { useDeleteWorkInviteMutation } from '@/features/work/api/delete-work-invite';
import { useShowFullArtistProfileQuery } from '@/features/work/api/show-full-artist-profile';
import { useClubProfileProgressQuery } from '@/shared/api/requests';
import { translate } from '@/shared/i18n/translate';

import { interactionWorkItemsStatusFilters } from '../../constants/filterStatuses';

export const useSendInviteView = ({
  clubId,
  workItem,
  artistId
}: {
  clubId: string;
  artistId: string;
  workItem: WorkItemDto | null;
}) => {
  const workInviteRemove = useDeleteWorkInviteMutation();
  const router = useRouter();
  const queryClient = useQueryClient();

  const clubProfileProgress = useClubProfileProgressQuery({
    profileId: clubId.toString()
  });

  const artistFullProfile = useShowFullArtistProfileQuery({
    profileId: artistId
  });
  const [cause, setCause] = useState<
    'incompleted' | 'artist-have-contract' | null
  >(null);

  useEffect(() => {
    if (
      clubProfileProgress.data?.data &&
      clubProfileProgress.data.data.totalCompletion < 100
    ) {
      setCause('incompleted');
    }
    if (
      artistFullProfile.data?.data?.status === ArtistProfileStatus.OnContract ||
      artistFullProfile.data?.data?.status ===
        ArtistProfileStatus.FinishingContract
    ) {
      setCause('artist-have-contract');
    }
  }, [clubProfileProgress, artistFullProfile.data?.data]);

  const canSendInvite =
    clubProfileProgress.data?.data?.totalCompletion === 100 &&
    (workItem === null ||
      workItem.status === WorkItemStatus.Rejected ||
      workItem.status === WorkItemStatus.Expired) &&
    (artistFullProfile === null ||
      (artistFullProfile.data?.data.status !== ArtistProfileStatus.OnContract &&
        artistFullProfile.data?.data.status !==
          ArtistProfileStatus.FinishingContract));

  const revokeInvite = async () => {
    if (workItem) {
      await workInviteRemove.mutateAsync(
        {
          params: {
            workInviteId: workItem.workItemId.toString()
          }
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: [
                'ClubWorkItemsList',
                workItem.clubProfileId.toString(),
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });

            notifications.show({
              title: translate('notifications.workItemIsCanceled'),
              ...notificationStyleConfig
            });

            if (workItem) {
              router.history.back();
            }
          }
        }
      );
    }
  };

  return {
    state: {
      canSendInvite,
      cause
    },
    functions: { revokeInvite }
  };
};
